import './Welcome.scss'

import { useState } from 'react'

import { Button, Col, Container, Form, Row, Spinner } from 'react-bootstrap'
import { isInApp, openUrlInSafari } from 'components/common/helpers/Mobile'

import RembleLogomark from '../../assets/images/Remble_Logomark-Green.png'
import { updateTOSAcceptance } from 'components/common/api/Users'
import { navigateTo } from 'components/common/navigation'
import { ROUTE_WELCOME } from 'components/common/helpers/Paths'

/**
 * Terms of Service Component. Used to display the Terms of Service acceptance screen for users that signed up
 * with oauth and still need to accept the TOS before they can use the app.
 */
export default function TOSAcceptance({ history }) {
  const [signupError, setSignupError] = useState()
  const [loading, setLoading] = useState(false)

  // Terms of Use
  const [termsOfUse, setTermsOfUse] = useState()
  const [isValidTermsOfUse, setIsValidTermsOfUse] = useState(true)

  /**
   * Helper function to handle clicks on privacy policy link
   * @param {Event} event The click event
   * @param {String} url The URL to open
   */
  const handlePrivacyPolicyClick = (event, url) => {
    // If we are in the app and the user clicks on the privacy policy link, let's open the link in a native window
    // TODO: Handle for Android
    if (isInApp()) {
      event.preventDefault()
      event.stopPropagation()

      openUrlInSafari(url)
    }
  }

  /**
   * Helper function to handle clicks on terms of use link
   * @param {Event} event The click event
   */
  const handleTermsOfUseClick = (event, url) => {
    // If we are in the app and the user clicks on the privacy policy link, let's open the link in a native window
    // TODO: Handle for Android
    if (isInApp()) {
      event.preventDefault()
      event.stopPropagation()

      openUrlInSafari(url)
    }
  }

  /**
   * Sets and runs validation on TOS field
   * @param {String} value The field's value
   */
  const setAndValidateTermsAcceptance = value => {
    const isValid = value

    setTermsOfUse(value)
    setIsValidTermsOfUse(isValid)
  }

  /**
   * Helper function to handle form submission
   * @param {Event} event The form submission event
   */
  const handleSubmit = async event => {
    event.preventDefault()
    setLoading(true)

    // Update the user's TOS acceptance
    const result = await updateTOSAcceptance()

    // Continue with the signup process
    if (result.success) {
      navigateTo(ROUTE_WELCOME, history)
      return
    }

    // If there was an error, set the error message
    if (result.error || result.detail) {
      setSignupError(result.error || result.detail)
    } else {
      setSignupError(
        'An unknown error occurred. Please try again or contact support at support@remble.com if the problem persists.'
      )
    }

    setLoading(false)
  }

  return (
    <Container className='welcome-container'>
      <Row>
        <Col>
          <img
            className='logomark'
            src={RembleLogomark}
            alt='Remble'
            width='25'
            fetchpriority='high'
          />
          <div className='mt-3'>
            <h1 className='title'>Before we begin</h1>
            <p className='subtitle'>
              You must accept the Remble Terms of Service and Privacy Policy to
              continue.
            </p>
          </div>
        </Col>
      </Row>
      <Form onSubmit={handleSubmit} noValidate>
        <Form.Group className='form-group' controlId='formTermsOfUse'>
          {/* Wrap checks in this div to fix Safari rendering issue */}
          <div className='form-floating'>
            <Form.Check
              name='terms_acceptance'
              type='checkbox'
              id={`check-termsOfUse`}
            >
              <Form.Check.Input
                type='checkbox'
                required
                onChange={e =>
                  setAndValidateTermsAcceptance(e.currentTarget.checked)
                }
                isInvalid={!isValidTermsOfUse}
                isValid={termsOfUse && isValidTermsOfUse}
              />
              {'  '}I agree to the Remble{' '}
              <a
                href='https://www.remble.com/terms-of-use'
                target='_blank'
                rel='noreferrer'
                onClick={e =>
                  handleTermsOfUseClick(
                    e,
                    'https://www.remble.com/terms-of-use'
                  )
                }
              >
                Terms of Use
              </a>{' '}
              and the{' '}
              <a
                href='https://www.remble.com/privacy'
                target='_blank'
                rel='noreferrer'
                onClick={e =>
                  handlePrivacyPolicyClick(e, 'https://www.remble.com/privacy')
                }
              >
                Privacy Policy
              </a>
              <Form.Control.Feedback type='valid'>
                Thank you!
              </Form.Control.Feedback>
              <Form.Control.Feedback type='invalid'>
                You must accept the Terms of Use in order to use Remble.
              </Form.Control.Feedback>
            </Form.Check>
          </div>
        </Form.Group>

        {/* Did we receive a signup error? */}
        {signupError && (
          <div className='error'>
            <i className='icon bi-exclamation-circle'></i>{' '}
            <strong>Error: </strong>
            {/* Do we have an array of messages or a string? */}
            {!Array.isArray(signupError) ? (
              String(signupError) // Force to string
            ) : (
              <>
                {signupError.map((error, i) => (
                  <span key={i}>{error.msg} </span>
                ))}
              </>
            )}
          </div>
        )}

        <Button
          variant='primary'
          size='lg'
          type='submit'
          disabled={loading || !termsOfUse}
          className='w-100 mt-5'
        >
          {loading ? (
            <>
              <Spinner
                as='span'
                animation='border'
                size='sm'
                role='status'
                aria-hidden='true'
              />
              <span className='px-2'>Loading...</span>
            </>
          ) : (
            <span>Continue</span>
          )}
        </Button>
      </Form>
    </Container>
  )
}
